import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { supportAPI } from '../../services/api';

const STATUS_CLASS = { open: 'bg-amber-100 text-amber-700', answered: 'bg-emerald-100 text-emerald-700', closed: 'bg-slate-200 text-slate-600' };

function TicketModal({ ticketId, onClose, onChanged }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [reply, setReply] = useState('');
  const [sending, setSending] = useState(false);
  const [closing, setClosing] = useState(false);
  const [error, setError] = useState('');
  const bottomRef = useRef(null);

  function load() {
    return supportAPI.adminTicket(ticketId).then((r) => setData(r)).catch(() => setError('Failed to load.'));
  }
  useEffect(() => { load().finally(() => setLoading(false)); }, [ticketId]);
  useEffect(() => { bottomRef.current?.scrollIntoView({ block: 'nearest' }); }, [data?.messages?.length]);

  async function handleReply(e) {
    e.preventDefault();
    if (!reply.trim()) return;
    setSending(true); setError('');
    try {
      await supportAPI.adminReply(ticketId, reply.trim());
      setReply('');
      await load();
      onChanged?.();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to send.');
    } finally {
      setSending(false);
    }
  }

  async function handleClose() {
    if (!window.confirm('Mark this ticket as closed?')) return;
    setClosing(true);
    try {
      await supportAPI.adminClose(ticketId);
      await load();
      onChanged?.();
    } catch {
      setError('Failed to close.');
    } finally {
      setClosing(false);
    }
  }

  const ticket = data?.ticket;

  // Portaled to document.body — this page sits inside Layout's
  // backdrop-blur-xl content card, which (like any transform/filter
  // ancestor) creates a new containing block for `position: fixed`,
  // shrinking the modal to that card's box instead of the full screen.
  return createPortal(
    <div className="fixed inset-0 z-50 flex items-end justify-center bg-slate-900/40 sm:items-center sm:px-4">
      <div className="flex h-[88dvh] w-full max-w-lg flex-col rounded-t-2xl bg-white shadow-xl sm:h-[80dvh] sm:rounded-2xl">
        <div className="shrink-0 border-b border-slate-100 px-4 py-3">
          <div className="flex items-start justify-between gap-2">
            <div className="min-w-0">
              <p className="truncate text-sm font-bold text-slate-800">{ticket?.full_name || '…'}</p>
              {ticket && <p className="truncate text-xs text-slate-400">Roll {ticket.roll_no || '—'} · {ticket.phone || ticket.email || 'no contact on file'}</p>}
            </div>
            <button onClick={onClose} className="shrink-0 rounded-full p-1.5 text-slate-400 hover:bg-slate-100">✕</button>
          </div>
          {ticket && (
            <div className="mt-2 flex items-center justify-between">
              <span className={`rounded-full px-2.5 py-1 text-xs font-semibold ${STATUS_CLASS[ticket.status] || 'bg-slate-100'}`}>{ticket.status}</span>
              {ticket.status !== 'closed' && (
                <button type="button" onClick={handleClose} disabled={closing}
                  className="rounded-lg border border-slate-200 px-2.5 py-1 text-xs font-semibold text-slate-500 hover:bg-slate-50 disabled:opacity-60">
                  Close ticket
                </button>
              )}
            </div>
          )}
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto px-4 py-3">
          {loading ? (
            <p className="py-10 text-center text-sm text-slate-400">Loading…</p>
          ) : (
            <div className="space-y-3">
              {(data?.messages || []).map((m, i) => (
                <div key={i} className={`flex ${m.sender_type === 'admin' ? 'justify-end' : 'justify-start'}`}>
                  <div className={`max-w-[85%] rounded-2xl px-4 py-2.5 text-[14px] leading-6 shadow-sm ${
                    m.sender_type === 'admin' ? 'bg-orange-500 text-white' : 'bg-slate-100 text-slate-800'
                  }`}>
                    <p className="whitespace-pre-wrap break-words">{m.message}</p>
                    <p className={`mt-1 text-[10px] ${m.sender_type === 'admin' ? 'text-orange-100' : 'text-slate-400'}`}>
                      {m.sender_type === 'admin' ? 'AWTF Support' : ticket?.full_name} · {m.created_at?.slice(0, 16).replace('T', ' ')}
                    </p>
                  </div>
                </div>
              ))}
              <div ref={bottomRef} />
            </div>
          )}
        </div>

        <div className="shrink-0 border-t border-slate-100 p-3 pb-[max(12px,env(safe-area-inset-bottom))]">
          {error && <p className="mb-2 text-xs text-red-600">{error}</p>}
          {ticket?.status === 'closed' ? (
            <p className="text-center text-xs text-slate-400">This ticket is closed.</p>
          ) : (
            <form onSubmit={handleReply} className="flex gap-2">
              <input value={reply} onChange={(e) => setReply(e.target.value)}
                className="min-w-0 flex-1 rounded-xl border border-slate-200 px-3 py-2.5 text-[16px] sm:text-sm" placeholder="Reply…" />
              <button type="submit" disabled={sending || !reply.trim()}
                className="shrink-0 rounded-xl bg-orange-600 px-4 py-2.5 text-sm font-semibold text-white disabled:opacity-60">
                {sending ? '…' : 'Send'}
              </button>
            </form>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
}

function NotifyEmailSettings() {
  const [email, setEmail] = useState('');
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState({ text: '', ok: true });

  useEffect(() => {
    supportAPI.getNotificationEmail().then((r) => setEmail(r.notification_email || '')).catch(() => {});
  }, []);

  async function handleSave(e) {
    e.preventDefault();
    setSaving(true);
    setMsg({ text: '', ok: true });
    try {
      await supportAPI.saveNotificationEmail(email);
      setMsg({ text: '✓ Saved', ok: true });
    } catch (err) {
      setMsg({ text: '✗ ' + (err.response?.data?.error || 'Save failed'), ok: false });
    } finally {
      setSaving(false);
    }
  }

  return (
    <form onSubmit={handleSave} className="flex flex-wrap items-center gap-2 rounded-2xl border border-slate-100 bg-white px-4 py-3 shadow-sm">
      <label className="text-xs font-semibold text-slate-600 shrink-0">Email me on new requests</label>
      <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="e.g. support@awtf.or.tz"
        className="min-w-0 flex-1 rounded-xl border border-slate-200 px-3 py-2 text-sm" />
      <button type="submit" disabled={saving}
        className="shrink-0 rounded-xl bg-slate-800 px-4 py-2 text-xs font-semibold text-white hover:bg-slate-900 disabled:opacity-60">
        {saving ? 'Saving…' : 'Save'}
      </button>
      {msg.text && <span className={`text-xs font-semibold ${msg.ok ? 'text-emerald-600' : 'text-red-600'}`}>{msg.text}</span>}
    </form>
  );
}

export default function Support() {
  const [status, setStatus] = useState('open');
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openId, setOpenId] = useState(null);

  function load() {
    setLoading(true);
    return supportAPI.adminList(status).then((r) => setTickets(r.tickets || [])).catch(() => {}).finally(() => setLoading(false));
  }
  useEffect(() => { load(); }, [status]);

  return (
    <section className="space-y-4">
      <div>
        <h2 className="text-xl font-bold text-slate-800">Support</h2>
        <p className="mt-1 text-sm text-slate-600">Requests from the Login page and from logged-in members.</p>
      </div>

      <NotifyEmailSettings />

      <div className="flex flex-wrap gap-2">
        {[['open', 'Open'], ['answered', 'Answered'], ['closed', 'Closed'], ['', 'All']].map(([key, label]) => (
          <button key={key} onClick={() => setStatus(key)}
            className={`rounded-xl border px-3 py-2 text-xs font-semibold ${status === key ? 'border-orange-500 bg-orange-50 text-orange-700' : 'border-slate-200 text-slate-600 hover:bg-slate-50'}`}>
            {label}
          </button>
        ))}
      </div>

      {loading ? (
        <p className="py-10 text-center text-sm text-slate-400">Loading…</p>
      ) : tickets.length === 0 ? (
        <div className="rounded-2xl border border-slate-100 bg-white p-10 text-center shadow-sm">
          <p className="text-slate-500">No tickets here.</p>
        </div>
      ) : (
        <div className="space-y-2">
          {tickets.map((t) => (
            <button key={t.id} onClick={() => setOpenId(t.id)}
              className="flex w-full items-center justify-between rounded-2xl border border-slate-100 bg-white px-4 py-3.5 text-left shadow-sm hover:bg-slate-50">
              <div className="min-w-0">
                <p className="truncate font-medium text-slate-800">{t.full_name} {t.roll_no ? `· Roll ${t.roll_no}` : ''}</p>
                <p className="truncate text-xs text-slate-400">{t.subject || (t.token ? 'Login page request' : 'In-portal request')} · {t.last_message_at?.slice(0, 16).replace('T', ' ')}</p>
              </div>
              <span className={`shrink-0 rounded-full px-2.5 py-1 text-xs font-semibold ${STATUS_CLASS[t.status] || 'bg-slate-100'}`}>
                {t.status}
              </span>
            </button>
          ))}
        </div>
      )}

      {openId && (
        <TicketModal ticketId={openId} onClose={() => setOpenId(null)} onChanged={load} />
      )}
    </section>
  );
}
